/**
 * ActionDetailModal Component
 *
 * Unified detail content component for action + shot visualization.
 * Manages:
 * - Data fetching from action_detail API (single endpoint for both actions and shots)
 * - Action filter state + shot filter state (cascading: action → shot)
 * - Chart toggle (Actions dot chart / Shots hex chart)
 * - Filter sidebar toggle (Action Filters / Shot Filters)
 * - Hierarchical breakdown supporting both action and shot filter keys
 *
 * This is the inner content component (no modal shell).
 * Used inside DetailModal.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Snackbar,
  Alert,
  Stack,
  Typography,
  CircularProgress,
  Skeleton,
  Fade,
} from "@mui/material";
import ErrorCard from "../../components/Elements/ErrorCard";
import DevSourceLabel from "../../components/Elements/DevSourceLabel";
import { useLeagueEnvironment } from "../../data/LeagueEnvironmentProvider";
import { ExplorerEnvironment } from "../../data/Cache";
import {
  Action,
  ActionDetailContentProps,
  ActionDetailResponse,
  ActionFilters,
  ChanceLineup,
  DEFAULT_ACTION_FILTERS,
  LINEUP_KEYS,
  LineupKey,
  isLineupKey,
  normalizeAction,
  Shot,
  ShotFilters,
  DEFAULT_SHOT_FILTERS,
  actionShotToShot,
  isShotFilterKey,
} from "./types";
import { PlayerTableProvider } from "./PlayerTableContext";
import {
  getVisibleCourtLength,
  COURT_CONSTANTS,
} from "../ShotDetailModal/types";
import { applyFilters as applyShotFilters } from "../ShotDetailModal/utils";
import { applyActionFilters, stripLineupFilters } from "./utils";
import ActionDetailChart from "./ActionDetailChart";
import ActionDetailFilters from "./ActionDetailFilters";
import ActionDetailSummary from "./ActionDetailSummary";
import ShotFilterChips from "../ShotDetailModal/ShotFilterChips";
import ShotDetailChart from "../ShotDetailModal/ShotDetailChart";
import { useLeagueApi } from "../../data/LeagueEnvironmentProvider";
import VideoPlayerDialog, { skippedVideoMessage } from "../../components/Elements/VideoPlayerDialog";
import type { VideoPlayerState } from "../../components/Elements/VideoPlayerDialog";
import {
  buildExplorerQuery,
  buildTeamTableStateParam,
  actionFiltersToExplorerTuples,
  shotFiltersToExplorerTuples,
  PLAYER_NAME_FILTERS,
  withPlayerIds,
  contextSeasons,
  mergeExplorerFilters,
} from "../utils/buildExplorerQuery";
import { FeatureFlags } from "../../util/featureFlags";
import "../ShotDetailModal/ShotDetailModal.scss";

// Calculate chart height to match sidebar — same formula as ShotDetailChart
const CHART_WIDTH = 600;
const CHART_HEIGHT = Math.round(
  CHART_WIDTH * (getVisibleCourtLength() / COURT_CONSTANTS.COURT_WIDTH),
);

// Stable empty-state references to avoid provider churn when data is absent.
const EMPTY_PLAYERS: string[] = [];
const EMPTY_LINEUPS: Record<string, ChanceLineup> = {};

/**
 * Unified detail content component. Fetches from action_detail API
 * (returns both actions and shots), manages cascading filter state,
 * and renders chart toggle + filter sidebar toggle.
 */
export function ActionDetailContent({
  queryContext,
  teamId,
  teamAbbrev,
  season,
  seasonType,
  showCollegeLine,
  showEPts = true,
  directData,
  modalTitle,
}: ActionDetailContentProps) {
  const api = useLeagueApi();
  const environment = useLeagueEnvironment();
  const isNBA = environment === ExplorerEnvironment.NBA;

  const [loading, setLoading] = useState(!directData);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ActionDetailResponse | null>(
    directData ?? null,
  );

  // Action filter state
  const [filters, setFiltersRaw] = useState<ActionFilters>(
    DEFAULT_ACTION_FILTERS,
  );

  // Track whether the user has manually interacted with the Action/No Action
  // filter. Until they do, actionPresence is auto-derived from whether
  // action-level filters are actually narrowing the data.
  const presenceManuallySet = useRef(false);

  // Wrap setFilters to detect manual actionPresence changes.
  const setFilters = useCallback(
    (next: ActionFilters | ((prev: ActionFilters) => ActionFilters)) => {
      setFiltersRaw((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;

        // Detect if the caller changed actionPresence directly
        const presenceChanged =
          resolved.actionPresence !== prev.actionPresence &&
          (resolved.actionPresence.size !== prev.actionPresence.size ||
            ![...resolved.actionPresence].every((v) =>
              prev.actionPresence.has(v),
            ));

        if (presenceChanged) {
          presenceManuallySet.current = true;
        }

        return resolved;
      });
    },
    [],
  );

  // Auto-derive actionPresence from whether action filters are narrowing the data.
  // Runs after every filter change. If action filters let all real actions through,
  // include no_action; if they exclude anything, remove no_action.
  // Skipped once the user manually touches the Action/No Action toggle.
  useEffect(() => {
    if (presenceManuallySet.current || !data) return;

    const realActions = data.actions.filter(
      (a) => a.actionType !== "no_action",
    );
    if (realActions.length === 0) return;

    // Test action-level filters against real actions (ignore actionPresence itself)
    const testFilters: ActionFilters = {
      ...filters,
      actionPresence: new Set<"action" | "no_action">(["action"]),
    };
    const filtered = applyActionFilters(realActions, testFilters, data.lineups);
    const isFiltering = filtered.length < realActions.length;

    const hasNoAction = filters.actionPresence.has("no_action");
    const shouldHaveNoAction = !isFiltering;

    if (shouldHaveNoAction !== hasNoAction) {
      setFiltersRaw((prev) => ({
        ...prev,
        actionPresence: shouldHaveNoAction
          ? new Set<"action" | "no_action">(["action", "no_action"])
          : new Set<"action" | "no_action">(["action"]),
      }));
    }
  }, [filters, data]);

  // Shot filter state (cascaded from action filters)
  const [shotFilters, setShotFilters] =
    useState<ShotFilters>(DEFAULT_SHOT_FILTERS);

  // View toggles
  const [chartView, setChartView] = useState<"shots" | "actions">("shots");
  const [filterView, setFilterView] = useState<"action" | "shot">("shot");

  // Summary expansion state — ordered hierarchy of group keys for breakdown
  const [expandedSummaryGroups, setExpandedSummaryGroups] = useState<string[]>(
    [],
  );

  // Row highlight state for chart ↔ summary interaction
  const [hoveredRow, setHoveredRow] = useState<{
    key: string;
    actions: Action[];
    shots?: Shot[];
  } | null>(null);
  const [selectedRow, setSelectedRow] = useState<{
    key: string;
    actions: Action[];
    shots?: Shot[];
  } | null>(null);

  // Effective highlight: hover overrides selection temporarily
  const highlightedActions = useMemo(() => {
    const source = hoveredRow ?? selectedRow;
    if (!source) return null;
    return new Set(source.actions);
  }, [hoveredRow, selectedRow]);

  // Effective shot subset for shot chart: row's shots override full linkedShots
  const highlightedShots = useMemo(() => {
    const source = hoveredRow ?? selectedRow;
    if (!source?.shots || source.shots.length === 0) return null;
    return source.shots;
  }, [hoveredRow, selectedRow]);

  // Real actions only (excludes no_action) — used for action dot chart rendering
  const chartActions = useMemo(
    () =>
      data ? data.actions.filter((a) => a.actionType !== "no_action") : [],
    [data],
  );

  const players = data?.players ?? EMPTY_PLAYERS;
  const lineups: Record<string, ChanceLineup> = data?.lineups ?? EMPTY_LINEUPS;

  // ---- Shot cascade pipeline ----
  // Step 1: Apply action filters → filteredActions
  const filteredActions = useMemo(
    () => (data ? applyActionFilters(data.actions, filters, data.lineups) : []),
    [data, filters],
  );

  // Step 2: Link shots to filtered actions via chanceId, convert ActionShot → Shot.
  // Backend now includes no_action entries for non-action chances, so all shots
  // have a matching action — no orphan detection needed.
  const linkedShots: Shot[] = useMemo(() => {
    if (!data || data.shots.length === 0) return [];
    const filteredChanceIds = new Set(
      filteredActions.map((a) => a.chanceId).filter(Boolean),
    );
    return data.shots
      .filter((s) => s.chanceId && filteredChanceIds.has(s.chanceId))
      .map((s) => actionShotToShot(s, data.players));
  }, [filteredActions, data]);

  // Only the lineup axes in the current breakdown hierarchy get stripped —
  // any other active lineup filters stay applied. Used to build both the
  // broadened shot pool for lineup breakdowns and the chart's filter set.
  const inBreakdownLineupKeys = useMemo(
    () => expandedSummaryGroups.filter(isLineupKey) as LineupKey[],
    [expandedSummaryGroups],
  );
  const hasLineupBreakdown = inBreakdownLineupKeys.length > 0;

  // Step 2b: Shot pool that mirrors filters EXCEPT the in-breakdown lineup
  // axes — powers on/off breakdowns where the "off" partition needs shots
  // from chances the stripped lineup filter would otherwise exclude.
  const rootLinkedShots: Shot[] = useMemo(() => {
    if (!data || data.shots.length === 0) return [];
    const noLineup = applyActionFilters(
      data.actions,
      stripLineupFilters(filters, inBreakdownLineupKeys),
      data.lineups,
    );
    const cids = new Set(noLineup.map((a) => a.chanceId).filter(Boolean));
    return data.shots
      .filter((s) => s.chanceId && cids.has(s.chanceId))
      .map((s) => actionShotToShot(s, data.players));
  }, [data, filters, inBreakdownLineupKeys]);

  // Chart inputs broaden to the breakdown-base pool so hover/click highlights
  // on lineup sub-rows resolve to visible dots/hexes.
  const chartFilters = useMemo(
    () =>
      hasLineupBreakdown
        ? stripLineupFilters(filters, inBreakdownLineupKeys)
        : filters,
    [filters, hasLineupBreakdown, inBreakdownLineupKeys],
  );
  const chartLinkedShots = hasLineupBreakdown ? rootLinkedShots : linkedShots;

  // Step 3: Apply shot filters → filteredShots (consumed by shot chart in Phase 3)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filteredShots: Shot[] = useMemo(
    () => applyShotFilters(linkedShots, shotFilters),
    [linkedShots, shotFilters],
  );

  // Reset shot filters when action filters change (shot pool changes)
  useEffect(() => {
    setShotFilters(DEFAULT_SHOT_FILTERS);
  }, [filters]);

  const handleRowHover = useCallback(
    (key: string | null, actions: Action[] | null, shots?: Shot[]) => {
      setHoveredRow(key && actions ? { key, actions, shots } : null);
    },
    [],
  );

  const handleRowClick = useCallback(
    (key: string, actions: Action[], shots?: Shot[]) => {
      setSelectedRow((prev) =>
        prev?.key === key ? null : { key, actions, shots },
      );
    },
    [],
  );

  // Clear selection when filters or breakdown groups change (rows are stale)
  useEffect(() => {
    setSelectedRow(null);
    setHoveredRow(null);
  }, [filters, expandedSummaryGroups]);

  // Whether any filter is active (differs from default) — drives reset button visibility
  const hasActiveFilters = useMemo(() => {
    const af = filters;
    const actionActive =
      af.actionPresence.size !== DEFAULT_ACTION_FILTERS.actionPresence.size ||
      af.transition.size !== DEFAULT_ACTION_FILTERS.transition.size ||
      af.chanceType.size !== DEFAULT_ACTION_FILTERS.chanceType.size ||
      af.zone.size !== DEFAULT_ACTION_FILTERS.zone.size ||
      af.actionType.size !== DEFAULT_ACTION_FILTERS.actionType.size ||
      af.actionSimple.size !== DEFAULT_ACTION_FILTERS.actionSimple.size ||
      af.actionSize.size !== DEFAULT_ACTION_FILTERS.actionSize.size ||
      af.direct.size !== DEFAULT_ACTION_FILTERS.direct.size ||
      af.primaryOutcome.size !== DEFAULT_ACTION_FILTERS.primaryOutcome.size ||
      af.primaryPlayer.size > 0 ||
      af.secondaryPlayer.size > 0 ||
      af.primaryDefender.size > 0 ||
      af.secondaryDefender.size > 0 ||
      LINEUP_KEYS.some((k) => af[k].size > 0) ||
      Object.values(af.typeSpecific).some((s) => s.size > 0);

    const sf = shotFilters;
    const shotActive =
      sf.assisted.size !== DEFAULT_SHOT_FILTERS.assisted.size ||
      sf.fouled.size !== DEFAULT_SHOT_FILTERS.fouled.size ||
      sf.shotType.size !== DEFAULT_SHOT_FILTERS.shotType.size ||
      sf.contestLevel.size !== DEFAULT_SHOT_FILTERS.contestLevel.size ||
      sf.catchAndShoot.size !== DEFAULT_SHOT_FILTERS.catchAndShoot.size ||
      sf.regionCategory.size !== DEFAULT_SHOT_FILTERS.regionCategory.size ||
      sf.shooter.size > 0 ||
      sf.passer.size > 0 ||
      (sf.contester?.size ?? 0) > 0;

    return actionActive || shotActive;
  }, [filters, shotFilters]);

  const handleResetAll = useCallback(() => {
    presenceManuallySet.current = false;
    setFiltersRaw(DEFAULT_ACTION_FILTERS);
    setShotFilters(DEFAULT_SHOT_FILTERS);
    setExpandedSummaryGroups([]);
  }, []);

  // Video playlist state
  const [videoState, setVideoState] = useState<VideoPlayerState | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);

  const handleVideoClick = useCallback(
    async (chanceIds: string[], rowLabel: string, metaKey?: boolean) => {
      setVideoLoading(true);
      setVideoError(null);
      try {
        const response = await api.getVideoPlaylistByChanceIds(chanceIds);
        if (response.error) {
          setVideoError(response.error);
        } else if (response.playlistUrl) {
          const titleParts = modalTitle ? [modalTitle] : [];
          if (rowLabel !== "Overall") titleParts.push(rowLabel);
          if (metaKey) {
            setVideoState({
              url: response.playlistUrl,
              title: titleParts.join("  \u00BB  "),
              skippedCount: response.skippedCount ?? 0,
            });
          } else {
            window.open(response.playlistUrl, "_blank");
            if (response.skippedCount) {
              setVideoError(skippedVideoMessage(response.skippedCount));
            }
          }
        }
      } catch (err: any) {
        const errorData = err?.response?.data;
        setVideoError(errorData?.error ?? "Failed to generate video playlist");
      } finally {
        setVideoLoading(false);
      }
    },
    [api, modalTitle],
  );

  // Open in Explorer handler
  const [explorerLoadingKey, setExplorerLoadingKey] = useState<string | null>(
    null,
  );
  const explorerBasePath =
    environment === ExplorerEnvironment.DRAFT
      ? "/explorer/draft"
      : "/explorer/nba";

  const handleOpenInExplorer = useCallback(
    async (extraDimensions: string[]) => {
      if (!queryContext) return;
      const dimKey = `__dim_${extraDimensions[0]}__`;
      setExplorerLoadingKey(dimKey);
      try {
        // Convert active action + shot filter state to explorer filter tuples
        let activeTuples = actionFiltersToExplorerTuples(filters, players);
        const shotTuples = shotFiltersToExplorerTuples(shotFilters);
        for (const [k, v] of shotTuples) activeTuples.set(k, v);
        if (
          Array.from(activeTuples.keys()).some((k) => PLAYER_NAME_FILTERS.has(k))
        ) {
          const { data: options } = await api.getPlayerFilterOptions({
            seasons: contextSeasons(queryContext, season).join(","),
          });
          activeTuples = withPlayerIds(activeTuples, options.values ?? []);
        }
        const extraFilters = mergeExplorerFilters(activeTuples);
        const query = buildExplorerQuery(queryContext, season, {
          extraFilters,
          extraDimensions,
        });
        const { data } = await api.registerQuery(query);
        const tsParam = `&ts=${buildTeamTableStateParam()}`;
        window.open(
          `${explorerBasePath}?q=${data.query_id}${tsParam}`,
          "_blank",
        );
      } catch (err) {
        setVideoError("Failed to open in Explorer");
      } finally {
        setExplorerLoadingKey(null);
      }
    },
    [api, queryContext, season, explorerBasePath, filters, shotFilters, players],
  );

  const handleExpandGroup = useCallback(
    (groupKey: string, isModifier: boolean) => {
      setExpandedSummaryGroups((prev) => {
        const index = prev.indexOf(groupKey);
        if (!isModifier) {
          // Regular click: replace with single level, or toggle off
          return prev.length === 1 && prev[0] === groupKey ? [] : [groupKey];
        }
        // Modifier (Cmd/Ctrl) click:
        if (index !== -1) {
          // Already in hierarchy — remove it and everything after
          return prev.slice(0, index);
        }
        if (prev.length >= 3) return prev; // Max depth

        // Nesting constraint: action keys cannot appear after shot keys
        const hasShotKey = prev.some(isShotFilterKey);
        if (hasShotKey && !isShotFilterKey(groupKey)) return prev; // Silently reject

        return [...prev, groupKey]; // Append new level
      });
    },
    [],
  );

  // Fetch data (only used when directData is not provided)
  const fetchData = useCallback(async () => {
    if (!teamId || !queryContext) return;
    setLoading(true);
    setError(null);

    try {
      const response = await api.getActionDetail(teamId, season, queryContext, seasonType);
      const normalized = response.actions.map(normalizeAction);
      setData({
        ...response,
        actions: normalized,
      });
    } catch (err: any) {
      console.error("Action detail error:", err?.response?.data ?? err);
      setError("Failed to load action data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [api, teamId, season, seasonType, queryContext]);

  // Fetch on mount/queryContext change (skip when using direct data)
  useEffect(() => {
    if (directData) return;
    fetchData();
    presenceManuallySet.current = false;
    setFiltersRaw(DEFAULT_ACTION_FILTERS);
  }, [fetchData, directData]);

  return (
    <PlayerTableProvider value={players}>
    <Box className="shot-detail-modal__content">
      {/* Loading State */}
      {loading && <ActionLoadingSkeleton />}

      {/* Error State */}
      {!loading && error && <ErrorCard message={error} onRetry={fetchData} />}

      {/* Content */}
      {!loading && !error && data && data.actions.length > 0 && (
        <Stack spacing={2}>
          {/* Summary Stats */}
          <ActionDetailSummary
            actions={data.actions}
            linkedShots={linkedShots}
            rootLinkedShots={rootLinkedShots}
            lineups={lineups}
            filters={filters}
            shotFilters={shotFilters}
            expandedGroups={expandedSummaryGroups}
            onRowHover={handleRowHover}
            onRowClick={handleRowClick}
            selectedRowKey={selectedRow?.key ?? null}
            showEPts={showEPts}
            onVideoClick={isNBA ? handleVideoClick : undefined}
            videoLoading={videoLoading}
          />

          {/* Chart and Filters side by side */}
          <Stack
            className="shot-detail-modal__chart-and-filters"
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems="flex-start"
          >
            {/* Chart with toggle */}
            <Box className="shot-detail-modal__chart-container">
              {/* Chart view toggle */}
              <Box sx={{ mb: 0.5, ml: 0.5 }}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: chartView === "actions" ? 700 : 400,
                    cursor: "pointer",
                    color:
                      chartView === "actions"
                        ? "text.primary"
                        : "text.disabled",
                  }}
                  onClick={() => setChartView("actions")}
                >
                  Actions
                </Typography>
                <Typography
                  component="span"
                  sx={{ mx: 0.75, color: "text.disabled", fontSize: "0.75rem" }}
                >
                  |
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: chartView === "shots" ? 700 : 400,
                    cursor: "pointer",
                    color:
                      chartView === "shots" ? "text.primary" : "text.disabled",
                  }}
                  onClick={() => setChartView("shots")}
                >
                  Shots
                </Typography>
              </Box>

              {chartView === "actions" ? (
                <ActionDetailChart
                  actions={chartActions}
                  lineups={lineups}
                  filters={chartFilters}
                  width={CHART_WIDTH}
                  showCollegeLine={showCollegeLine}
                  highlightedActions={highlightedActions}
                />
              ) : (
                <ShotDetailChart
                  shots={highlightedShots ?? chartLinkedShots}
                  filters={shotFilters}
                  width={CHART_WIDTH}
                  showCollegeLine={showCollegeLine}
                />
              )}
            </Box>

            {/* Filters Sidebar */}
            <Box
              className="shot-detail-modal__sidebar"
              sx={{ height: { xs: "auto", md: CHART_HEIGHT } }}
            >
              {/* Filter view toggle + reset button */}
              <Box
                sx={{
                  mb: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: filterView === "action" ? 700 : 400,
                      cursor: "pointer",
                      color:
                        filterView === "action"
                          ? "text.primary"
                          : "text.disabled",
                    }}
                    onClick={() => setFilterView("action")}
                  >
                    Action Filters
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      mx: 0.75,
                      color: "text.disabled",
                      fontSize: "0.75rem",
                    }}
                  >
                    |
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: filterView === "shot" ? 700 : 400,
                      cursor: "pointer",
                      color:
                        filterView === "shot"
                          ? "text.primary"
                          : "text.disabled",
                    }}
                    onClick={() => setFilterView("shot")}
                  >
                    Shot Filters
                  </Typography>
                </Box>
                <Fade in={hasActiveFilters}>
                  <Button
                    size="small"
                    onClick={handleResetAll}
                    disabled={!hasActiveFilters}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      py: 0,
                      minWidth: "auto",
                      lineHeight: 1.2,
                    }}
                  >
                    Reset filters
                  </Button>
                </Fade>
              </Box>

              <Box className="shot-detail-modal__filters-scroll">
                {filterView === "action" ? (
                  <ActionDetailFilters
                    actions={data.actions}
                    lineups={lineups}
                    filters={filters}
                    onChange={setFilters}
                    expandedGroups={expandedSummaryGroups}
                    onExpandGroup={handleExpandGroup}
                    onResetGroups={() => setExpandedSummaryGroups([])}
                    onExplorerDimensionClick={
                      FeatureFlags.teamReportExplorerLinks && queryContext
                        ? (dim) => handleOpenInExplorer([dim])
                        : undefined
                    }
                    explorerLoadingKey={explorerLoadingKey}
                  />
                ) : (
                  <ShotFilterChips
                    shots={chartLinkedShots}
                    filters={shotFilters}
                    onChange={setShotFilters}
                    expandedGroups={expandedSummaryGroups}
                    onExpandGroup={handleExpandGroup}
                  />
                )}
              </Box>
            </Box>
          </Stack>

          {/* Query time footer */}
          <Typography
            variant="caption"
            color="text.disabled"
            className="shot-detail-modal__query-time"
          >
            {data.actions.length.toLocaleString()} actions | Query time:{" "}
            {data.queryTimeSeconds.toFixed(2)}s
            {data.resolution && (
              <DevSourceLabel resolution={data.resolution} sx={{ ml: 1 }} />
            )}
          </Typography>
        </Stack>
      )}

      {/* Empty State */}
      {!loading && !error && data && data.actions.length === 0 && (
        <Box className="empty-state">
          <Typography color="text.secondary">
            No actions found for this filter context.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try selecting a different row in the report.
          </Typography>
        </Box>
      )}

      {/* Video playlist iframe dialog */}
      <VideoPlayerDialog
        state={videoState}
        onClose={() => setVideoState(null)}
        zIndex={1400}
      />

      {/* Video playlist error snackbar */}
      <Snackbar
        open={Boolean(videoError)}
        autoHideDuration={5000}
        onClose={() => setVideoError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setVideoError(null)}
          sx={{ width: "100%" }}
        >
          {videoError}
        </Alert>
      </Snackbar>
    </Box>
    </PlayerTableProvider>
  );
}

export default ActionDetailContent;

// ============================================================================
// Loading Skeleton
// ============================================================================

function ActionLoadingSkeleton() {
  const aspectRatio = getVisibleCourtLength() / COURT_CONSTANTS.COURT_WIDTH;

  return (
    <Stack spacing={2}>
      {/* Summary skeleton */}
      <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />

      {/* Chart and Filters side by side */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
        {/* Court skeleton */}
        <Box
          sx={{
            position: "relative",
            flex: "0 0 auto",
            width: 520,
          }}
        >
          <Skeleton
            variant="rectangular"
            sx={{
              width: "100%",
              paddingTop: `${aspectRatio * 100}%`,
              borderRadius: 1,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <CircularProgress size={32} />
          </Box>
        </Box>

        {/* Filter skeleton */}
        <Stack spacing={1.5} sx={{ flex: "1 1 auto", minWidth: { md: 220 } }}>
          <Stack spacing={0.5}>
            <Skeleton variant="text" width={70} height={16} />
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Skeleton variant="rounded" width={55} height={24} />
              <Skeleton variant="rounded" width={65} height={24} />
              <Skeleton variant="rounded" width={55} height={24} />
              <Skeleton variant="rounded" width={45} height={24} />
            </Stack>
          </Stack>
          <Stack spacing={0.5}>
            <Skeleton variant="text" width={50} height={16} />
            <Stack direction="row" spacing={0.5}>
              <Skeleton variant="rounded" width={60} height={24} />
              <Skeleton variant="rounded" width={60} height={24} />
            </Stack>
          </Stack>
          <Stack spacing={0.5}>
            <Skeleton variant="text" width={55} height={16} />
            <Stack direction="row" spacing={0.5}>
              <Skeleton variant="rounded" width={50} height={24} />
              <Skeleton variant="rounded" width={50} height={24} />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
